import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
// import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/src/assets/icons/dashboard/navigate-arrow.svg';
import currentTimeZone from '../../../../config/timeZone';
import ThemeContext from '../../../../contexts/theme-context/ThemeContext';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import NoDataAvailable from '../../../../shared-components/no-data-available/NoDataAvailable';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import { getRelativeTime, removeEmptyKeys } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

// Sequential green ramp - magnitude encoding: one hue, monotone light->dark.
// Derived (not eyeballed) from this app's own categorical green (#008300,
// slot 4 at index 8 below): same 13 OKLCH lightness steps as the dataviz
// skill's documented blue ramp, re-anchored on green's hue/chroma so the
// light->dark spacing matches the already-shipped, already-used ramp. Keep
// in sync with the .legend-gradient stops in device-utilization-chart.scss.
const SEQUENTIAL_GREEN_RAMP = [
  '#94fd8c',
  '#84ed7c',
  '#75de6e',
  '#65ce5e',
  '#55bf4f',
  '#45b040',
  '#34a02f',
  '#20911c',
  '#008300',
  '#007400',
  '#006600',
  '#005800',
  '#004a00',
];

const HOUR_LABEL_INTERVAL = 3;

interface HeatmapCell {
  hour: number;
  utilizationPercent: number;
  powerConsumedKwh: number;
}

interface HeatmapRow {
  date: string;
  cells: HeatmapCell[];
}

interface HoveredCellState {
  date: string;
  hour: number;
  utilizationPercent: number;
  powerConsumedKwh: number;
  coords: { x: number; y: number; isTop: boolean };
}

interface DeviceUtilizationChartProperties {
  startDate: string;
  endDate: string;
  // Accepted for prop-compatibility with the parent dashboard, which passes
  // the same filters object to every chart - intentionally unused here since
  // the backend report is domain-wide only (see the component doc comment).
  // eslint-disable-next-line react/no-unused-prop-types
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Maps a utilization value onto the sequential green ramp. True zero gets no
 * fill at all (recedes to the card surface) rather than the ramp's lightest
 * step, so empty cells read as empty. In dark mode the ramp direction flips
 * so near-zero still anchors toward the (dark) surface and high values stay
 * the vivid/light end.
 * @param {number} value - Cell utilization percent.
 * @param {number} maxValue - Max utilization percent across the whole grid.
 * @param {boolean} isDark - Whether the app is in dark theme.
 * @returns {string | undefined} A ramp hex color, or undefined for no fill.
 */
const getUtilizationColor = (
  value: number,
  maxValue: number,
  isDark: boolean,
): string | undefined => {
  if (!value || value <= 0 || maxValue <= 0) return undefined;
  const ramp = isDark ? [...SEQUENTIAL_GREEN_RAMP].reverse() : SEQUENTIAL_GREEN_RAMP;
  const ratio = Math.min(value / maxValue, 1);
  const index = Math.round(ratio * (ramp.length - 1));
  return ramp[index];
};

/**
 * Device utilization heatmap: X = hour of day, Y = calendar date, cell color
 * = utilization %, power consumed (kWh) shown on hover. Rendered as a custom
 * CSS grid (not a chart library) for full control over the sequential color
 * ramp, true-zero handling, and light/dark theming. `filters` is accepted
 * (the parent dashboard passes the same prop to every chart) but intentionally
 * unused - the backend report is domain-wide only.
 * @param {any} param0 - The param0
 * @param {any} param0.startDate - The startDate prop
 * @param {any} param0.endDate - The endDate prop
 * @returns {any} The result
 */
function DeviceUtilizationChart({ startDate, endDate }: DeviceUtilizationChartProperties) {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const [rows, setRows] = useState<HeatmapRow[]>([]);
  const [maxValue, setMaxValue] = useState<number>(0);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [hoveredCell, setHoveredCell] = useState<HoveredCellState | null>(null);
  const activeRangeReference = React.useRef<string>('');

  /**
   * Handles hovering or focusing on a heatmap cell to calculate and display tooltip portal.
   * @param {React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>} event - The trigger mouse or focus event.
   * @param {string} date - Date string of the target cell.
   * @param {HeatmapCell} cell - The cell data object.
   * @returns {void}
   */
  const handleCellHover = (
    event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
    date: string,
    cell: HeatmapCell,
  ): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isTop = rect.top >= 90;
    setHoveredCell({
      date,
      hour: cell.hour,
      utilizationPercent: cell.utilizationPercent,
      powerConsumedKwh: cell.powerConsumedKwh,
      coords: {
        x: rect.left + rect.width / 2,
        y: isTop ? rect.top - 8 : rect.bottom + 8,
        isTop,
      },
    });
  };

  /**
   * Clears the active hovered cell tooltip.
   * @returns {void}
   */
  const handleCellLeave = (): void => {
    setHoveredCell(null);
  };

  /**
   * Fetch the domain-wide device utilization heatmap for the selected date range.
   * @param {string} rangeStartDate - The range start date
   * @param {string} rangeEndDate - The range end date
   * @param {boolean} hardRefresh - Whether to bypass the backend's cache
   */
  const fetchPowerConsumedReport = async (
    rangeStartDate: string,
    rangeEndDate: string,
    hardRefresh = false,
  ) => {
    const rangeKey = `${rangeStartDate}_${rangeEndDate}`;
    activeRangeReference.current = rangeKey;
    try {
      setIsLoading(true);
      const response = await dashboard.getDeviceUtilizationReport(
        removeEmptyKeys({
          startDate: rangeStartDate,
          endDate: rangeEndDate,
          timeZone: currentTimeZone,
          hardRefresh,
        }),
      );

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== rangeKey) return;

      const { dates = [], data = [], last_updated_time: updatedTime } = response.data || {};

      const cellsByDate = new Map<string, HeatmapCell[]>();
      let observedMax = 0;
      data.forEach((cell: any) => {
        if (!cellsByDate.has(cell.date)) cellsByDate.set(cell.date, []);
        cellsByDate.get(cell.date)?.push({
          hour: cell.hour,
          utilizationPercent: cell.utilizationPercent,
          powerConsumedKwh: cell.powerConsumedKwh,
        });
        if (cell.utilizationPercent > observedMax) observedMax = cell.utilizationPercent;
      });

      // ✅ Update the chart with the right data
      setRows(dates.map((date: string) => ({ date, cells: cellsByDate.get(date) || [] })));
      setMaxValue(observedMax);
      if (updatedTime) {
        setLastUpdatedTime(updatedTime);
      }
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === rangeKey) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport(startDate, endDate, true);
  }, [startDate, endDate]);

  const isEmpty = rows.length === 0 || maxValue === 0;

  return (
    <div className="dashboard-device-utilization-chart-wrap" dir={i18n.dir()}>
      <div className="dashboard-device-utilization-chart-header">
        <div className="header-title">
          <div className="title-wrapper">
            <h6>
              {t('utilization')} ({t('device')})
            </h6>
            <TooltipIcon text={t('utilizationChargerChart')} infoColor="info" />
          </div>
          <div className="refresh">
            <div className="refresh-icon">
              {isRefreshing ? (
                <div className="loading">
                  <LoaderIcon style={{ width: '20px', height: '20px' }} />
                </div>
              ) : (
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={() => {
                    fetchPowerConsumedReport(
                      startDate,
                      endDate,
                      true, // hard refresh on button click
                    );
                    setIsRefreshing(true);
                  }}
                  svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
                />
              )}
            </div>
            <p className="updated-time">
              {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}
            </p>
          </div>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/operations/chargers')}>
            {/* <NavigateArrow /> */}
          </span>
        </div>
      </div>
      <div className="dashboard-device-utilization-chart-container">
        {isLoading ? (
          <center className="device-utilization-loader">
            <BatteryLoader />
          </center>
        ) : null}

        {!isLoading && isEmpty && <NoDataAvailable />}

        {!isLoading && !isEmpty && (
          <>
            <div className="utilization-heatmap-grid" onScroll={handleCellLeave}>
              <div className="heatmap-hour-labels">
                <span className="heatmap-row-label" />
                {Array.from({ length: 24 }, (_, hour) => (
                  <span key={hour} className="heatmap-hour-label">
                    {hour % HOUR_LABEL_INTERVAL === 0 ? `${String(hour).padStart(2, '0')}:00` : ''}
                  </span>
                ))}
              </div>
              {rows.map((row) => (
                <div key={row.date} className="heatmap-row">
                  <span className="heatmap-row-label">{dayjs(row.date).format('MMM D')}</span>
                  {row.cells.map((cell) => (
                    <div
                      key={cell.hour}
                      className="heatmap-cell"
                      role="button"
                      tabIndex={0}
                      aria-label={`${dayjs(row.date).format('MMM D, YYYY')}, ${String(cell.hour).padStart(2, '0')}:00 - ${cell.utilizationPercent.toFixed(2)}%`}
                      onMouseEnter={(event) => handleCellHover(event, row.date, cell)}
                      onMouseLeave={handleCellLeave}
                      onFocus={(event) => handleCellHover(event, row.date, cell)}
                      onBlur={handleCellLeave}
                      style={{
                        backgroundColor: getUtilizationColor(
                          cell.utilizationPercent,
                          maxValue,
                          isDark,
                        ),
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="heatmap-legend">
              <span className="legend-label">{t('low', 'Low')}</span>
              <span className="legend-gradient" />
              <span className="legend-label">{t('high', 'High')}</span>
            </div>
          </>
        )}
        {hoveredCell &&
          ReactDOM.createPortal(
            <div
              className={`heatmap-cell-portal-tooltip ${
                hoveredCell.coords.isTop ? 'position-top' : 'position-bottom'
              }`}
              style={{
                left: `${hoveredCell.coords.x}px`,
                top: `${hoveredCell.coords.y}px`,
              }}>
              <p className="tooltip-period">
                {dayjs(hoveredCell.date).format('MMM D, YYYY')},{' '}
                {String(hoveredCell.hour).padStart(2, '0')}:00
              </p>
              <p>
                {t('utilization')}: {hoveredCell.utilizationPercent.toFixed(2)}%
              </p>
              <p>
                {t('powerConsumed', 'Power Consumed')}: {hoveredCell.powerConsumedKwh} kW
              </p>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

DeviceUtilizationChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default DeviceUtilizationChart;
